/**
 * Created by jonas on 2021-02-02.
 */
import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    BetWayWinData,
    ClientTorpedoPart,
    ClientXNudge,
    ClientXWays,
    DasUBootGameData,
    DasUBootInitData,
    DasUBootRoundType,
    isDasUBootGameData,
    isDasUBootInitData
} from "./DasUBootData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {StateMatrix, StateMatrixCoordinate} from "../utils/StateMatrix";
import {DasUBootSymbolStates} from "../reels/DasUBootSymbol";
import {ArrayUtils} from "../utils/ArrayUtils";
import {DasUBootGameMode} from "./DasUBootGameMode";
import {DasUBootStatics} from "../utils/DasUBootStatics";
import {ModelUtils} from "../utils/ModelUtils";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {DasUBoot} from "../DasUBoot";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";

export interface WinLineData {
    betWayWin:BetWayWinData,
    winCoordinates:StateMatrixCoordinate[],
    lineMatrix:StateMatrix<boolean>
}


export interface PossibleWinLineData {
    matrix:StateMatrix<boolean>,
    reels:boolean[]
}

export interface PossibleWinData {
    lines:PossibleWinLineData[],
    combinedMatrix:StateMatrix<boolean>,
    combinedReels:boolean[]
}


export interface WinData {
    lines:WinLineData[],
    matrix:StateMatrix<DasUBootSymbolStates>
}


export interface ClientGameData {
    visibleReelsMatrix:StateMatrix<string>;
    xNudge:ClientXNudge[];
    xWays:ClientXWays[];
    xBomb:boolean[];
    torpedoParts:[ClientTorpedoPart?, ClientTorpedoPart?, ClientTorpedoPart?, ClientTorpedoPart?];
    torpedoSizes:number[],
    reels:string[][];
    multipliers:{
        total:number,
        stickyFSMultiplier:number,
        payoutMultiplier:number
    },
    totalPossibleWinWays:number,
    scatterCountAtReel:number[];
    scattersCollectedAtReel:number[];
    avalancheScattersPositions:number[];
    preAvalancheScattersPositions:number[];
    winData:WinData;

}

export class DasUBootModel extends GameModel<DasUBootInitData, DasUBootGameData> {

    public clientData:ClientGameData;
    public winCapReached:boolean = false;
    public periscopeNearWin:boolean[];
    public possibleWinData:PossibleWinData;
    public periscopeConnect2:StateMatrixCoordinate[];


    get totalWays():number {
        return this._totalWays;
    }

    get winRatio():number {
        return this._winRatio;
    }

    get winRatioNonMultiplied():number {
        return this._winRatioNonMultiplied;
    }

    public visibleReelsMatrix:StateMatrix<string>;
    public possibleWinWays:number[];


    private _totalWays:number;
    public isRestore:boolean = false;
    private _winRatio:number;
    private _winRatioNonMultiplied:number;

    public xBombCollapseMatrix:StateMatrix<number>;
    public avalancheDropInReels:string[][];

    public isRestoreFeatureBet : boolean;
    public buyFeatureName : string;


    public get reelsEnteringFreespin():string[][] {

        const matrix:StateMatrix<string> = new StateMatrix<string>([
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"],
            ["L1", "L2", "L3", "L4", "L5", "M1", "M2", "M3", "M4", "M5"]
        ]);

        if (this.data.reelsEnteringFreespin != undefined) {
            for (let i:number = 0; i < this.data.reelsEnteringFreespin.length; i++) {
                const reel:string[] = this.data.reelsEnteringFreespin[i];
                matrix.matrix[i].splice(3, reel.length, ...reel);
            }
        }

        return matrix.matrix;

    }

    constructor(api:ApiPlugin, triggerEvents:boolean = true) {
        super(api, triggerEvents);

        api.events.once("featureBet", (data : boolean)=>{
            this.isRestoreFeatureBet = data;
        });
        api.events.once("featureName", (data : string)=>{
            this.buyFeatureName = data;
        });
        DasUBoot.apiPlugin.events.on(APIEvent.BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < DasUBoot.model.initData.featureBuyTimesBetValue.length; index++) {
                if (DasUBoot.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });

        DasUBoot.apiPlugin.events.on(APIEvent.ACTION_SPINS_BET, (betData: APIFeatureBetEventData) => {
            for (let index = 0; index < DasUBoot.model.initData.featureBuyTimesBetValue.length; index++) {
                if (DasUBoot.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    Logger.logDev("Opted Feature : ", this.buyFeatureName);
                    break;
                }
            }
        });
    }

    public resetFeatureBuy():void {
        this.buyFeatureName = "";
        this.isRestoreFeatureBet = false;
    }

    public resetVisibleReels(reels:string[][]) {
        const stateMatrix = new StateMatrix<string>(reels);
        stateMatrix.matrix = ModelUtils.trimMatrixToGameAreaSizeWithPadding(stateMatrix.matrix, DasUBootStatics.defaultGameAreaSize);
        this.visibleReelsMatrix.matrix = stateMatrix.matrix;
        this.clientData.scatterCountAtReel = ModelUtils.calculateScatterIndexAtReel(this.visibleReelsMatrix);
        this.clientData.scattersCollectedAtReel = ModelUtils.calculateScatterCountCollectedAtReel(this.visibleReelsMatrix);
    }


    private fixApplyCollapsedSymbolsToReelData(data:DasUBootGameData, gameAreaSize:number[], collapsedAreaAfterBomb:string[][] | undefined):string[][] {
        //4x6 in NORMAL, //8x6 in FS
        const modifiedReels = ArrayUtils.cloneMatrix(data.reels)
        if (collapsedAreaAfterBomb){
            //Applying collapsed directly to reels including "X"
            for (let i:number = 0; i < gameAreaSize.length; i++){
                const beforePadding = (data.roundType == DasUBootRoundType.FREE_SPIN && i == 2) ? 1 : 3;
                modifiedReels[i].splice(beforePadding, collapsedAreaAfterBomb[i].length, ...collapsedAreaAfterBomb[i])
            }


            //Replacing "X" with new symbols
            for (let i:number = 0; i < modifiedReels.length; i++){
                for (let j:number = 0; j < modifiedReels[i].length; j++){
                    if (modifiedReels[i][j] == "X") {
                        modifiedReels[i][j] = data.reels[i][j];
                    }
                }
            }
        }




        return modifiedReels;

    }


    private replaceMYSTERY2Symbol(data:DasUBootInitData | DasUBootGameData){
        if (data.reels[5][3] === "MYSTERY2"){
            data.reels[5][3] = isDasUBootGameData(data) ? data.evaluatedArea[5][0] : "L5";
        }

        if (data.reels[5][4] === "MYSTERY2"){
            data.reels[5][4] = isDasUBootGameData(data) ? data.evaluatedArea[5][1] : "L5";
        }


        for (let i:number = 0; i < data.reels[5].length; i++){
            if (data.reels[5][i] === "MYSTERY2"){
                data.reels[5][i] = "L5";
            }
        }

        return data;
    }

    private createClientData(data:DasUBootInitData | DasUBootGameData) {

        data = this.replaceMYSTERY2Symbol(data);

        this.visibleReelsMatrix = new StateMatrix<string>(data.reels);
        this.visibleReelsMatrix.matrix = ModelUtils.trimMatrixToGameAreaSizeWithPadding(this.visibleReelsMatrix.matrix, data.gameAreaSize);


        if (isDasUBootGameData(data) && data.nextMode == DasUBootGameMode.AVALANCHE){
            this.xBombCollapseMatrix = ModelUtils.processCollapse(data.evaluatedArea, data.nextMode);
            this.xBombCollapseMatrix.matrix = ModelUtils.trimColumnsToMatchMatrix(this.xBombCollapseMatrix.matrix, this.visibleReelsMatrix.matrix);
        }

        if (isDasUBootGameData(data) && data.mode == DasUBootGameMode.AVALANCHE){

            data.reels = this.fixApplyCollapsedSymbolsToReelData(data, this.data.gameAreaSize, this.data.collapsedAreaAfterBomb);

            this.visibleReelsMatrix = new StateMatrix<string>(data.reels);
            this.visibleReelsMatrix.matrix = ModelUtils.trimMatrixToGameAreaSizeWithPadding(this.visibleReelsMatrix.matrix, data.gameAreaSize);
        }



        let reels = ArrayUtils.cloneMatrix(data.reels);

        if (isDasUBootInitData(data) && data.isRestoreState){
            //4x6 in NORMAL, //8x6 in FS
            if (data.collapsedAreaAfterBomb){
                for (let i:number = 0; i < data.gameAreaSize.length; i++){
                    const beforePadding = (data.roundType == DasUBootRoundType.FREE_SPIN && i == 2) ? 1 : 3;
                    reels[i].splice(beforePadding, data.collapsedAreaAfterBomb[i].length, ...data.collapsedAreaAfterBomb[i])
                }
            }


            if (data.periscope && data.periscope.hit){
                const periscopeStack = ["PW", "PW", "PW", "PW", "PW", "PW", "PW", "PW"];
                reels[2].splice(1, periscopeStack.length, ...periscopeStack);
            }

            if (data.xNudges){
                data.xNudges.doReelNudge = [false,false];
            }

            this.visibleReelsMatrix = new StateMatrix<string>(reels);
            this.visibleReelsMatrix.matrix = ModelUtils.trimMatrixToGameAreaSizeWithPadding(this.visibleReelsMatrix.matrix, data.gameAreaSize);
        }



        //Needs to be calculated before xWay/xNudge etc
        const winData = ModelUtils.createWinData(data, this.visibleReelsMatrix);
        const torpedoParts = ModelUtils.findTorpedoParts3(this.visibleReelsMatrix, data.mode, this.data.torpedo,  data.torpedo);
        const xBombReels = [false, false, false, false, false, false];

        const stickyFSMultiplier = data.stickyFSMultiplier | 0;
        const payoutMultiplier = isDasUBootGameData(data) ? data.payoutMultiplier : 0;


        let scatterCountAtReel = ModelUtils.calculateScatterIndexAtReel(this.visibleReelsMatrix, data);
        let scattersCollectedAtReel = ModelUtils.calculateScatterCountCollectedAtReel(this.visibleReelsMatrix, data);


        //Fixes scatter count order during Avalanche. So that the last scatter can land on a lower index reel than already collected scatters.
        let avalancheScattersPositions = [-1,-1,-1,-1,-1,-1];
        let preAvalancheScattersPositions = [-1,-1,-1,-1,-1,-1];
        if (this.gameData){
            preAvalancheScattersPositions = this.gameData.scatterPositions.concat();
        }
        if (data.mode == DasUBootGameMode.AVALANCHE && isDasUBootGameData(data)){
            scatterCountAtReel = this.clientData.scatterCountAtReel; //Reset to before avalanche.
            let currentHighestScatter = Math.max(...scatterCountAtReel);

            if (isDasUBootGameData(data)){
                for (let i:number = 0; i < data.scatterPositions.length; i++){
                    if (data.scatterPositions[i] > -1 && scatterCountAtReel[i] == 0){
                        currentHighestScatter += 1;
                        scatterCountAtReel[i] = currentHighestScatter;
                        avalancheScattersPositions[i] = data.scatterPositions[i];
                    }
                }
            }


        }


        this.clientData = {
            visibleReelsMatrix:this.visibleReelsMatrix,
            reels:reels,
            xWays:ModelUtils.createClientXWayData(data.mystery),
            xNudge:ModelUtils.createClientXNudgeData(winData.matrix, data.xNudges),
            xBomb:xBombReels,
            torpedoParts:torpedoParts,
            torpedoSizes:ModelUtils.getTorpedoSizes(data.torpedo),

            multipliers:{
                total:stickyFSMultiplier + payoutMultiplier,
                stickyFSMultiplier:stickyFSMultiplier,
                payoutMultiplier:payoutMultiplier
            },
            totalPossibleWinWays: 0,
            scatterCountAtReel:scatterCountAtReel,
            scattersCollectedAtReel:scattersCollectedAtReel,
            avalancheScattersPositions:avalancheScattersPositions,
            preAvalancheScattersPositions:preAvalancheScattersPositions,
            winData:winData
        }

        this.clientData.totalPossibleWinWays = ModelUtils.calculateTotalPossibleWinWays(this.clientData, data);
        this.clientData.reels = ModelUtils.findStackedWilds(this.clientData, true);

        if (data.mode == DasUBootGameMode.AVALANCHE) {
            const prevRoundDeleteCount = this.calculateDeleteCount(this.data.gameAreaSize, this.data.collapsedAreaAfterBomb); //Use old data to calculate base on prev round reels.
            this.processDropInSymbols(data.roundType, this.clientData.reels, prevRoundDeleteCount);

        }


        const bombCoords = this.visibleReelsMatrix.getAllCoordinateForValue("B");
        for (let bombCoord of bombCoords) {
            if (bombCoord.row == 4 && data.roundType == DasUBootRoundType.SUPER_FREE_SPIN){
                //Do not count pos 4 during SFS. If there is a bomb there it's behind the torpedo symbols and are not included in the win.
                continue;
            }
            this.clientData.xBomb[bombCoord.column] = true;
        }


        Logger.logDev("ClientData:", this.clientData);

    }

    protected onInit(data:DasUBootInitData):void {
        super.onInit(data);

        this.isRestore = data.isRestoreState;
        this.createClientData(data);
        if (data.torpedo?.isComplete) {
            this.clientData.torpedoParts = [];
        }

        this.possibleWinWays = ModelUtils.calculatePossibleWinWays(this.clientData.xWays, data.roundType == DasUBootRoundType.FREE_SPIN);

        Logger.logDev("INIT DATA", data);
    }

    protected onGame(data:DasUBootGameData):void {
        this.createClientData(data);
        super.onGame(data);
        Logger.logDev("GAME DATA", data);

        data.nearWinReels = this.fixNearWinData(data);

        this.isRestore = false;
        this._totalWays = ModelUtils.calculateTotalWays(data);

        this.periscopeConnect2 = this.getPeriscopeConnect2();
        //console.log(this.periscopeConnect2)



        this.possibleWinData = this.calculatePossibleWinData2(data);

        this.periscopeNearWin = [false, false, false, false, false, false];
        if (data.mode == DasUBootGameMode.FREESPIN) {
            this.periscopeNearWin[2] = this.possibleWinData.combinedReels[1]; //Periscope reel
            if (data.periscope?.hit) {
                this.periscopeNearWin[3] = this.possibleWinData.combinedReels[2];
                this.periscopeNearWin[4] = this.possibleWinData.combinedReels[3];
                this.periscopeNearWin[5] = this.possibleWinData.combinedReels[4];
            }
        }


        this.possibleWinWays = ModelUtils.calculatePossibleWinWays(this.clientData.xWays, data.roundType == DasUBootRoundType.FREE_SPIN);

        this._winRatio = data.totalSpinWinnings / data.playedBetValue;
        this._winRatioNonMultiplied = data.totalSpinWinningsNonMultiplied / data.playedBetValue;

        const accumulatedWinRatio = data.accumulatedRoundWin / data.playedBetValue;
        this.winCapReached = accumulatedWinRatio >= 55200;


    }


    private calculatePossibleWinData2(data:DasUBootGameData):PossibleWinData {

        const combinedReels:boolean[] = [false, false, false, false, false, false];
        const combinedMatrix:StateMatrix<boolean> = new StateMatrix();
        combinedMatrix.initWithValue(6, 10, false);

        const lines:PossibleWinLineData[] = [];
        for (let winLine of this.clientData.winData.lines){
            const lineMatrix = new StateMatrix<boolean>();
            lineMatrix.initWithValue(6,10,false);
            lineMatrix.setValueForCoordinates(winLine.lineMatrix.getAllCoordinateForValue(true), true);

            //Fix for M symbols.
            if (data.roundType != DasUBootRoundType.FREE_SPIN && data.mode != DasUBootGameMode.AVALANCHE) {
                for (let xWay of this.clientData.xWays) {
                    //Skip if M symbol breaks line.
                    let posCount = 0;

                    lineMatrix.matrix[xWay.reel][xWay.position] = false;

                    const possibleCoords = lineMatrix.getCoordinatedForValueInColumn(xWay.reel, true);
                    if (possibleCoords.length < 1) {
                        //M beaks line here
                        for (let i:number = xWay.reel; i < lineMatrix.matrix.length; i++) {
                            lineMatrix.setValueForColumn(i, false);
                        }
                        break;
                    }
                }
            }


            //Fix for stacked wild.
            for (let nudge of this.clientData.xNudge){
                lineMatrix.setValueForColumn(nudge.reel, false);
                lineMatrix.matrix[nudge.reel][nudge.w4StartPos] = true;
            }

            combinedMatrix.setValueForCoordinates(lineMatrix.getAllCoordinateForValue(true), true);

            lines.push({
                matrix :lineMatrix,
                reels: lineMatrix.isValueInColumns(true)
            });
        }


        const posCords = combinedMatrix.getAllCoordinateForValue(true);
        for (let pos of posCords) {
            combinedReels[pos.column] = true;
        }

        return {
            lines:lines,
            combinedMatrix:combinedMatrix,
            combinedReels:combinedReels
        };
    }



    private getPeriscopeConnect2():StateMatrixCoordinate[] {
        const searchMatrix:StateMatrix<string> = this.visibleReelsMatrix;
        const firstReelSymbols:string[] = searchMatrix.matrix[0].concat();
        let result:StateMatrixCoordinate[] = [];

        const stateMatrix:StateMatrix<boolean> = new StateMatrix();
        stateMatrix.initWithValue(2,3,false);

        for (let i:number = 0; i < searchMatrix.matrix[1].length; i++){
            let value = searchMatrix.matrix[1][i];

            if (value =="M" && this.clientData.xWays[0]){
                value = this.clientData.xWays[0].symbol;
            }

            if (value == firstReelSymbols[0]){
                stateMatrix.matrix[0][0] = true;
                stateMatrix.matrix[1][i] = true;
            }
            if (value == firstReelSymbols[1]){
                stateMatrix.matrix[0][1] = true;
                stateMatrix.matrix[1][i] = true;
            }
        }

        result = stateMatrix.getAllCoordinateForValue(true);

        return result;
    }

    private calculateDeleteCount(gameAreaSize:number[], collapsedAreaAfterBomb?:string[][]):number[] {
        const result = [0, 0, 0, 0, 0, 0];
        if (collapsedAreaAfterBomb) {
            for (let i:number = 0; i < collapsedAreaAfterBomb.length; i++) {
                const reel = collapsedAreaAfterBomb[i];
                const deleted = reel.filter((value:string, index:number) => {
                    return (value == "X" || value == "-") && index < gameAreaSize[i];
                });
                result[i] = deleted.length;
            }
        }
        return result
    }

    public processDropInSymbols(roundType:DasUBootRoundType, reels:string[][], deleteCount:number[]) {

            this.avalancheDropInReels = []
            for (let i:number = 0; i < deleteCount.length; i++) {
                const beforePadding = (roundType == DasUBootRoundType.FREE_SPIN && i == 2) ? 1 : 3;
                const symbols = reels[i].slice(beforePadding, deleteCount[i] + beforePadding)
                this.avalancheDropInReels.push(symbols);
            }

    }

    private fixNearWinData(data:DasUBootGameData):boolean[] {
        const result = [false,false,false,false,false,false];

        if (data.wasFeatureBuy){
            //No near win on feature buy.
            return result;
        }


        if (data.mode == DasUBootGameMode.AVALANCHE){
            let scatterCount = 0;
            for (let pos of this.clientData.preAvalancheScattersPositions){
                if (pos >= 0){
                    scatterCount++;
                }
            }

            for(let i:number = 1; i < this.clientData.scattersCollectedAtReel.length-1; i++){
                if (scatterCount >= 2 && this.clientData.preAvalancheScattersPositions[i] < 0){
                    result[i] = true;
                }

                if (this.clientData.avalancheScattersPositions[i] >= 0){
                    scatterCount++;
                }

            }
        } else  {
            //Normal spin near win
            for(let i:number = 2; i < this.clientData.scattersCollectedAtReel.length-1; i++){
                if (this.clientData.scattersCollectedAtReel[i] >= 2){
                    result[i+1] = true;
                }
            }
        }



        result[5] = false; //Last reel is always false. There is no scatters on reel 5.

        return result;
    }
}